import React, {useState} from "react";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import {makeStyles} from "@material-ui/core/styles";
import HelpIcon from "@material-ui/icons/Help";
import {Autocomplete} from "@material-ui/lab";

import ConfirmDialog from "common_components/confirm_dialog";
import Expando from "./expando";
import ActionArgs from "./action_args";
import {getErrorStatusForArgValue} from "utils/flowmaster/get_error_status_for_arg_value";
import ActionArgument from "./action_argument";

import {
  Workflow,
  WorkflowContext,
  WorkflowInputs,
  Prompt,
} from "common/flowmaster/types/workflow";
import {
  WorkflowTask,
  ActionDefinition,
  TaskArgument,
} from "common/flowmaster/types/task_config";
import {OrganisationId} from "common/types/organisation";
import {Topic} from "common/types/topic";
import {DataLocation} from "common/flowmaster/types/data_location";

interface TaskProps {
  task: WorkflowTask;
  onUpdateItem: (data: Record<string, unknown>) => void;
  onDeleteItem: () => void;
  context: WorkflowContext;
  inputs: WorkflowInputs;
  prompts: Prompt[];
  actionDefinitions: ActionDefinition[];
  priorTasks: WorkflowTask[];
  futureTasks: WorkflowTask[];
  workflows: Workflow[];
  organisationId: OrganisationId;
  embeddingTopics: Topic[];
  forceOpen?: boolean;
  isInlineWorkflowTask: boolean;
  disabled?: boolean;
}

interface TaskEditorProps extends Omit<TaskProps, "onUpdateItem"> {
  selectedActionDefinition: ActionDefinition | undefined;
  onUpdateArg: (index: number, value: TaskArgument) => void;
  onUpdateName: (name: string) => void;
  onUpdateAction: (newAction: string | null) => void;
  handleActionArgError: (key: string, hasError: boolean) => void;
  disabled?: boolean;
}

const Task: React.FC<TaskProps> = ({
  task,
  onUpdateItem,
  onDeleteItem,
  context,
  inputs,
  prompts,
  actionDefinitions,
  workflows,
  priorTasks,
  futureTasks,
  organisationId,
  embeddingTopics,
  forceOpen,
  isInlineWorkflowTask,
  disabled,
}) => {
  const selectedActionDefinition = actionDefinitions.find(
    actionDefinition => actionDefinition.key === task.action,
  );

  const [localTask, setLocalTask] = useState<WorkflowTask>(task);
  const [actionDefinitionErrors, setActionDefinitionErrors] = useState(
    Object.fromEntries(
      selectedActionDefinition?.args?.map((arg, index) => [
        arg.key,
        getErrorStatusForArgValue(task.args[index]),
      ]) ?? [],
    ),
  );

  const handleActionArgError = (argKey, value) => {
    if (actionDefinitionErrors[argKey] === value) {
      return;
    }

    setActionDefinitionErrors({
      ...actionDefinitionErrors,
      [argKey]: value,
    });
  };

  const handleArgUpdate = (index: number, value: TaskArgument) => {
    setLocalTask(prevTask => {
      const args =
        !Array.isArray(prevTask.args) || index >= prevTask.args.length
          ? addNewArg(prevTask.args, index, value)
          : prevTask.args.map((arg, i) => (i === index ? value : arg));

      const updatedTask = {...prevTask, args};
      onUpdateItem(updatedTask);
      return updatedTask;
    });
  };

  const addNewArg = (
    args: TaskArgument[],
    index: number,
    value: TaskArgument,
  ) => {
    const newArgs = Array.isArray(args) ? [...args] : [];
    newArgs[index] = value;
    return newArgs;
  };

  const handleNameUpdate = (name: string) => {
    setLocalTask(prevTask => {
      const updatedTask = {...prevTask, name};
      onUpdateItem(updatedTask);
      return updatedTask;
    });
  };

  const handleActionUpdate = (newAction: string | null) => {
    setLocalTask(prevTask => {
      if (newAction === null) {
        return prevTask;
      }
      const newArgs = initialiseArgs(newAction, actionDefinitions);
      if (newArgs) {
        const updatedTask = {
          ...prevTask,
          action: newAction,
          args: newArgs as TaskArgument[],
        };
        onUpdateItem(updatedTask as Record<string, unknown>);
        return updatedTask;
      }
      return prevTask;
    });
  };

  return (
    <Expando
      beginExpanded={forceOpen}
      error={Object.values(actionDefinitionErrors).some(value => value)}
    >
      <TaskSummary task={localTask} futureTasks={futureTasks} />
      <TaskEditor
        task={localTask}
        selectedActionDefinition={selectedActionDefinition}
        onUpdateArg={handleArgUpdate}
        onUpdateName={handleNameUpdate}
        onUpdateAction={handleActionUpdate}
        onDeleteItem={onDeleteItem}
        actionDefinitions={actionDefinitions}
        workflows={workflows}
        priorTasks={priorTasks}
        futureTasks={futureTasks}
        context={context}
        inputs={inputs}
        prompts={prompts}
        organisationId={organisationId}
        embeddingTopics={embeddingTopics}
        isInlineWorkflowTask={isInlineWorkflowTask}
        handleActionArgError={handleActionArgError}
        disabled={disabled}
      />
    </Expando>
  );
};

interface TaskSummaryProps {
  task: WorkflowTask;
  futureTasks: WorkflowTask[];
}
function TaskSummary({task, futureTasks}: TaskSummaryProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <div
        style={{display: "flex", flexDirection: "row", alignItems: "center"}}
      >
        <Chip style={{marginRight: "0.5em"}} size="small" label={task.id} />
        <span>{task.name}</span>
      </div>
      <UsedBy task={task} futureTasks={futureTasks} />
    </div>
  );
}

const useStyles = makeStyles(() => ({
  helpTooltip: {
    maxWidth: "50em",
  },
}));
const TaskEditor: React.FC<TaskEditorProps> = ({
  task,
  selectedActionDefinition,
  onUpdateArg,
  onUpdateName,
  onUpdateAction,
  onDeleteItem,
  context,
  inputs,
  prompts,
  actionDefinitions,
  workflows,
  priorTasks,
  futureTasks,
  organisationId,
  embeddingTopics,
  isInlineWorkflowTask,
  handleActionArgError,
  disabled,
}) => {
  const [showingDeleteDialog, showDeleteDialog] = useState<boolean>(false);
  const [name, setName] = useState<string>(task.name);

  const taskActionDefinition =
    actionDefinitions.find(definition => definition.key === task.action) ??
    null;

  const sortedActionDefinitions = sortActionDefinitionsByGroup(
    actionDefinitions,
  );

  const classes = useStyles();
  return (
    <fieldset disabled={disabled} style={{width: "100%"}}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <div style={{display: "flex", alignItems: "center", flexWrap: "wrap"}}>
          <Chip style={{marginRight: "0.5em"}} size="small" label={task.id} />
          <TextField
            style={{width: "25em"}}
            value={name}
            label="Name"
            onChange={event => setName(event.target.value)}
            onBlur={() => onUpdateName(name)}
          />
          <Autocomplete
            style={{width: "15em"}}
            value={taskActionDefinition}
            onChange={(event, newActionDefinition) => {
              onUpdateAction(
                newActionDefinition?.key ?? taskActionDefinition?.key ?? null,
              );
            }}
            options={sortedActionDefinitions}
            getOptionLabel={actionDefinition => actionDefinition.name}
            renderInput={params => (
              <TextField {...params} label="Action" variant="standard" />
            )}
            groupBy={actionDefinition => actionDefinition.group}
          />
          {selectedActionDefinition ? (
            <Tooltip
              classes={{tooltip: classes.helpTooltip}}
              leaveDelay={10 * 1000}
              title={
                <div style={{fontSize: "1.5em"}}>
                  <pre style={{whiteSpace: "pre-wrap", lineHeight: "1.2"}}>
                    {selectedActionDefinition.help}
                  </pre>
                </div>
              }
            >
              <HelpIcon style={{marginLeft: "1em", color: "#888"}} />
            </Tooltip>
          ) : null}
        </div>
        <UsedBy task={task} futureTasks={futureTasks} />
      </div>

      <fieldset style={{margin: "0.5em 0"}}>
        <legend style={{fontStyle: "italic", color: "#555"}}>Arguments</legend>
        {selectedActionDefinition?.args?.map((argDefinition, index) => {
          return (
            <div
              key={index}
              style={{
                backgroundColor: isInlineWorkflowTask ? "#cfcf97" : "#bbb",
                margin: "0.5em",
                padding: "0.5em",
              }}
            >
              <ActionArgument
                argDefinition={argDefinition}
                argValue={task.args[index]}
                index={index}
                onUpdateItem={value => onUpdateArg(index, value)}
                priorTasks={priorTasks}
                futureTasks={futureTasks}
                context={context}
                inputs={inputs}
                prompts={prompts}
                actionDefinitions={actionDefinitions}
                workflows={workflows}
                organisationId={organisationId}
                embeddingTopics={embeddingTopics}
                handleHasError={value =>
                  handleActionArgError(argDefinition.key, value)
                }
                disabled={disabled}
                useListItem={false}
              />
            </div>
          );
        })}
      </fieldset>
      <div style={{display: "flex", justifyContent: "flex-end"}}>
        <Button variant="contained" onClick={() => showDeleteDialog(true)}>
          Delete
        </Button>
      </div>
      <ConfirmDialog
        open={showingDeleteDialog}
        onSuccess={() => onDeleteItem()}
        onClose={() => showDeleteDialog(false)}
        title="Delete Input"
        description="Are you sure you want to delete this task?"
        okButtonCaption="Yes"
        cancelButtonCaption="No"
      />
    </fieldset>
  );
};

function isDataLocationString(value: unknown): value is DataLocation<string> {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if ("source_type" in value && value.source_type === "task") {
    return true;
  }
  return false;
}

function isDataLocationTask<T>(task, dataLocation: DataLocation<T>): boolean {
  if (dataLocation?.source_type === "task") {
    return dataLocation?.task === task.id;
  }
  return false;
}

function UsedBy({task, futureTasks}: TaskSummaryProps) {
  const usedBy = futureTasks.filter(
    item =>
      Array.isArray(item?.args) &&
      item?.args?.find(arg => {
        return (
          arg?.value &&
          typeof arg?.value === "object" &&
          (("source_type" in arg.value &&
            isDataLocationString(arg.value) &&
            isDataLocationTask(task, arg?.value)) ||
            Object.values(arg.value).find(item =>
              isDataLocationTask(task, item),
            ) ||
            ("inputValues" in arg.value &&
              Object.values(arg.value?.inputValues).find(item =>
                isDataLocationTask(task, item),
              )))
        );
      }),
  );

  return usedBy.length ? (
    <span
      style={{
        marginLeft: "1em",
        fontStyle: "italic",
        color: "#666",
      }}
    >
      Used by: {usedBy.map(item => `"${item.name}"`).join(", ")}
    </span>
  ) : null;
}

function initialiseArgs(action: string, actionDefinitions: ActionDefinition[]) {
  const actionDefinition = actionDefinitions.find(({key}) => key === action);
  const val = actionDefinition?.args?.map(arg => ActionArgs[arg.type].default);
  return val;
}

function sortActionDefinitionsByGroup(
  actionDefinitions: ActionDefinition[],
): ActionDefinition[] {
  const groupedActionDefinitions: {
    [key: string]: ActionDefinition[];
  } = actionDefinitions.reduce((groups, action) => {
    groups[action.group] = groups[action.group] ?? [];
    groups[action.group].push(action);
    return groups;
  }, {});
  return Object.keys(groupedActionDefinitions)
    .sort((a, b) => {
      const isAExternal = a.startsWith("External");
      const isBExternal = b.startsWith("External");

      if (isAExternal && !isBExternal) {
        return -1; // a comes before b
      } else if (!isAExternal && isBExternal) {
        return 1; // b comes before a
      } else if (isAExternal && isBExternal) {
        return a.localeCompare(b); // both are external, sort alphabetically
      } else {
        return a.localeCompare(b); // both are non-external, sort alphabetically
      }
    })
    .map(group => [
      ...groupedActionDefinitions[group].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    ])
    .flat();
}

export default Task;
